import { useMemo } from "react";
import { DocumentRecordType, ExtractedRecord, ResourceName } from "@/types";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { usePartner } from "@/providers/partner/PartnerProvider";
import { todayLocalYMD } from "@/utils/formatters/dateFormatter";
import { getRecordData, assertsAnyYtd, reportedScopeFromFlags, type ReportedScope } from "../utils/recordUtils";
import { summarizeRecords } from "../utils/recordSummary";
import type { DocumentReviewProjection, RecordSummaryPayload } from "@/types/documentTransformation";

interface UseReviewAsOfStateProps {
  recordType?: DocumentRecordType;
  extractedRecords?: ExtractedRecord[];
  reviewProjection?: DocumentReviewProjection | null;
  /** Current reviewer-entered paid-through (as-of) value, YYYY-MM-DD or "". */
  reportedAsOf: string;
  /**
   * The transformation's server-computed record summary, source of the
   * whole-document reported-YTD answer (#6903).
   *
   * REQUIRED, and `undefined` must be passed explicitly rather than omitted.
   * The value decides whether the paid-through-date control renders at all, and
   * a control that silently fails to render hands claims authority to the
   * carrier-reported side (ADR-051). An optional prop would let a new caller
   * inherit that by forgetting a keystroke — the shape of the #6848 regression,
   * where an optional cascade prop defaulted a panel into the broken path.
   *
   * Pass `undefined` only when there genuinely is no summary; the hook then
   * falls back to scanning `extractedRecords`, correct only for the pre-#6755
   * transformations that are loaded whole.
   */
  recordSummary: RecordSummaryPayload | undefined;
  /**
   * Whether the loaded record set is INCOMPLETE (more pages remain).
   *
   * Only consulted when there is no `recordSummary`, and REQUIRED for the same
   * reason that prop is: getting it wrong hides the control, and a hidden
   * control hands claims authority to the reported side silently.
   *
   * The fix this closes assumed a summary-less document is always loaded whole
   * ("pre-#6755, so small"). It is not — `DocumentReviewTab` computes
   * `!recordSummary && hasMoreRecords` precisely because a paged summary-less
   * document exists, and on it the client mirror reduces over a silently-cut
   * page and returns a confident "no YTD mapped" (!4677 review,
   * `[AEM-mirror-partial]`; the #4478 class named in
   * frontend_list_fetching_standard.md §169).
   */
  recordsPartial: boolean;
}

/**
 * Derives the paid-through (as-of) authority signals shared by both the legacy
 * review header (`DocumentReviewHeader`) and the redesigned review context bar
 * (`ReviewContextBar`, #6099). Extracted so the ADR-051 (as-of authority) and
 * ADR-077 (Rx carve-out double-count) rules have a single home and the two
 * presentations can't drift.
 *
 * Pure derivation from the extracted records + projection + entered value, plus
 * the permission/read-only disable gate (ADR-044 / ADR-035).
 */
export interface ReviewAsOfState {
  /** Reviewer-local calendar date (YYYY-MM-DD); native picker max. */
  today: string;
  /** True when the entered value is later than today — blocks approval. */
  isInFuture: boolean;
  /**
   * Latest claim paid date in the document (YYYY-MM-DD, "" when none). From
   * the projection's whole-document `latest_paid_date` when the server sent
   * one (#7439); otherwise the loaded records, which on a paged document is a
   * partial answer — see latestPaidCheckPartial.
   */
  latestClaimPaidDate: string;
  /**
   * True when latestClaimPaidDate came from the LOADED page of an incomplete
   * record set, so a later paid date may exist on a page not yet fetched. The
   * stale check's scope disclosure reads this, not `recordsPartial` alone: once
   * the server answers over the whole document there is nothing partial to
   * disclose.
   */
  latestPaidCheckPartial: boolean;
  /** True when the entered value predates the latest in-doc claim paid date. */
  isStale: boolean;
  /** True when this document maps any carrier YTD amount. */
  hasMappedYtd: boolean;
  /** Whether the as-of control is relevant at all (non-reimbursement + mapped YTD). */
  showAsOf: boolean;
  /** True on an Rx carve-out policy (drives the scope label). */
  isCarveOut: boolean;
  /** The document's detected claim-type scope (label content on carve-out). */
  reportedScope: ReportedScope | undefined;
  /** Non-blocking as-of warning (stale or missing-for-YTD), if any. */
  asOfWarningMessage?: string;
  /** Non-blocking Rx double-count warning (ADR-077 §8), if any. */
  doubleCountWarningMessage?: string;
  /** True when the control should be disabled (no edit perm / read-only partner). */
  isDisabled: boolean;
}

export const useReviewAsOfState = ({
  recordType,
  extractedRecords,
  reviewProjection,
  reportedAsOf,
  recordSummary,
  recordsPartial,
}: UseReviewAsOfStateProps): ReviewAsOfState => {
  const isReimbursementDoc = recordType === DocumentRecordType.REIMBURSEMENTS;

  // `today` is the reviewer's LOCAL calendar date — comparing a native
  // date-input value against a UTC date (toISOString) is a frame off-by-one for
  // negative-offset timezones and could let a genuinely future date slip the
  // gate. Inline (not memoized) so it can't go stale across midnight.
  const today = todayLocalYMD();
  const isInFuture = !!reportedAsOf && reportedAsOf > today;

  // Latest claim paid date — drives the stale-as-of card: a paid-through date
  // that predates claims already in the document is suspicious but allowed.
  //
  // WHOLE-DOCUMENT first (#7439). The projection's latest_paid_date is computed
  // over the complete record set (at finalize for windowed reads, over the
  // full set for whole-document reads). The loaded-records scan below is the
  // FALLBACK, and on a paged document it is a partial answer: a later paid
  // date past the loaded page was a silent false negative, which is exactly
  // what the server field exists to remove. Absent from the server means
  // unknown (no claim line has a date, or a pre-#7439 summary on a windowed
  // read), never "none" — so the scan still runs and its scope is disclosed.
  const loadedLatestPaidDate = useMemo(() => {
    let max = "";
    for (const record of extractedRecords ?? []) {
      const paid = getRecordData(record).paid_date;
      if (typeof paid === "string" && paid && paid > max) max = paid;
    }
    return max;
  }, [extractedRecords]);
  const serverLatestPaidDate =
    typeof reviewProjection?.latest_paid_date === "string" ? reviewProjection.latest_paid_date : "";
  const latestClaimPaidDate = serverLatestPaidDate || loadedLatestPaidDate;
  const latestPaidCheckPartial = !serverLatestPaidDate && recordsPartial;
  const isStale = !!reportedAsOf && !isInFuture && !!latestClaimPaidDate && reportedAsOf < latestClaimPaidDate;

  // Rx carve-out (ADR-077 §3): on a carve-out policy, label the as-of control
  // with the document's detected claim-type scope so the reviewer knows which
  // type this single date covers. isCarveOut (policy shape) gates WHETHER to
  // label; reportedScope (doc's mapped YTD fields) is the label CONTENT.
  const isCarveOut = !!reviewProjection?.rx_carve_out;

  // Whole-document reported-YTD flags (#6903).
  //
  // From the SERVER, computed by the worker over the complete record set at
  // finalize. This used to scan `extractedRecords` in the browser, and since
  // #6755 paged the review surface that is a fraction of a large document: when
  // the YTD-bearing records sat past the loaded page the scan concluded "no YTD
  // is mapped", `showAsOf` went false, and the control never rendered. A
  // missing as-of makes the reported side win claims authority unconditionally
  // (ADR-051), so the reviewer silently lost a money decision on exactly the
  // documents too large to eyeball.
  //
  // `undefined` here is NOT "nothing mapped" — it is "nobody computed it", and
  // the two get opposite treatment below.
  const reportedYtdFlags = useMemo(() => {
    if (recordSummary) return recordSummary.reported_ytd;

    // No summary AND an incomplete record set: the mirror would reduce over a
    // silently-cut page and hand back a confident "nothing mapped". That is
    // UNKNOWN, not empty, so it routes into the fail-open branch below.
    //
    // An earlier version of this returned the mirror unconditionally, on the
    // premise that a summary-less transformation predates #6755 and is
    // therefore loaded whole. The premise is false, and DocumentReviewTab
    // contradicts it 60 lines from where it reads the summary — it computes
    // `!recordSummary && hasMoreRecords` because a paged summary-less document
    // reads "1,000 records extracted" above a footer saying "Showing 1,000 of
    // 497,711". So the fix reproduced the very defect it closes, on its own
    // fallback path (!4677 review, `[AEM-mirror-partial]`).
    if (recordsPartial) return undefined;

    // Complete set and no summary: the genuine pre-#6755 case, where the scan
    // IS the whole document. A real determination, honoured either way.
    return summarizeRecords(extractedRecords).reported_ytd;
  }, [recordSummary, recordsPartial, extractedRecords]);

  // The label content on a carve-out policy. Undefined when the flags are
  // absent — an unlabelled control is a small loss; see showAsOf for why the
  // control itself still renders.
  const reportedScope = reportedScopeFromFlags(reportedYtdFlags);

  // The as-of is only meaningful when this document actually maps a carrier YTD
  // amount — without one there's no reported value for the date to attach to
  // (per PR #3752).
  //
  // Previously this tested `ytd_total_paid` alone, so a medical-only or Rx-only
  // carve-out file — which by definition has no top-level total — hid the
  // control entirely and the reviewer could not enter a paid-through date at
  // all. A missing as-of makes the reported side win authority unconditionally,
  // so the per-type toggle (#5731) could never fire (#6238).
  const hasMappedYtd = assertsAnyYtd(reportedYtdFlags);

  // FAILS OPEN, which is the opposite of every other #6755-era gate and is
  // deliberate (#6903).
  //
  // Absent flags mean the transformation was finalized before #6903 shipped, so
  // there is no whole-document answer and no way to compute one from a page.
  // The two ways to be wrong are not symmetric:
  //
  //   - showing the control on a document with no YTD costs the reviewer a
  //     blank optional field they ignore;
  //   - hiding it on a document that HAS YTD silently changes which side wins
  //     claims authority, with nothing on screen saying so.
  //
  // So an unknown answer renders the control. The window gate fails CLOSED
  // because there, refusing to answer was safe; here, refusing to render is the
  // harm itself.
  const isReportedYtdUnknown = reportedYtdFlags === undefined;
  const showAsOf = !isReimbursementDoc && (hasMappedYtd || isReportedYtdUnknown);

  // Rx carve-out double-count warning (ADR-077 §8): a carve-out policy where the
  // document reports a total that is NOT broken out into a separate Rx figure and
  // exceeds its medical portion (or a bare total) may already include Rx that will
  // also load as separately carved-out line items. Mirrors the BE
  // CombinedReportedRxRisk predicate. Non-blocking confirm-before-approve.
  // Whole-document too (#6903): this warning is gated on showAsOf, so a hidden
  // control took the double-count check down with it on the same documents.
  // Cannot be reconstructed from the three presence flags — it is a per-record
  // comparison (total > medical), so one offending record among thousands is
  // the risk, and the worker computes it over the complete set.
  const hasCombinedTotalRisk = !!reportedYtdFlags?.combined_total_risk;
  const doubleCountWarningMessage =
    showAsOf && isCarveOut && hasCombinedTotalRisk
      ? "This is an Rx carve-out policy and the document reports a total that isn't broken out into a " +
        "separate Rx figure. That total may already include Rx that will also load as separately " +
        "carved-out line items — confirm it isn't double-counting Rx before approving."
      : undefined;

  // A carrier YTD amount is mapped but the reviewer left the as-of blank. The
  // reported total still becomes authoritative on approve, and with no as-of
  // newer claim lines can never override it (ADR-051 §2, #7392). Non-blocking,
  // but worth a confirm so it isn't silent.
  // Gated on hasMappedYtd, NOT showAsOf: on the fail-open branch we do not know
  // that a YTD is mapped, and asserting "this document maps a carrier YTD
  // total" about a document that may map none would be a warning we cannot
  // stand behind.
  const isMissingForYtd = showAsOf && hasMappedYtd && !reportedAsOf;

  // One non-blocking as-of warning, by priority (stale and missing are mutually
  // exclusive — one needs a value, the other needs it blank).
  const asOfWarningMessage = isStale
    ? `The paid-through date you entered is earlier than the latest claim paid date in this document. Confirm it reflects the carrier's reporting period.`
    : isMissingForYtd
      ? `This document maps a carrier YTD total, but no paid-through (as-of) date is set. On approve the carrier total still becomes the authoritative value, and without a date newer claim lines can't take over. Set the paid-through date unless that is intended.`
      : undefined;

  // Permission + read-only gating (ADR-044 via useResourcePermissions; ADR-035
  // disables under a read-only AEGL/ACAP partner — the server-side write guard
  // is enforcement of record, this just avoids offering an action that 400s).
  const { canEdit: canEditDocuments } = useResourcePermissions(ResourceName.DOCUMENTS);
  const { partner } = usePartner();
  const isDisabled = !canEditDocuments || !!partner?.readOnly;

  return {
    today,
    isInFuture,
    latestClaimPaidDate,
    latestPaidCheckPartial,
    isStale,
    hasMappedYtd,
    showAsOf,
    isCarveOut,
    reportedScope,
    asOfWarningMessage,
    doubleCountWarningMessage,
    isDisabled,
  };
};
